import React, { useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { graphql } from '@apollo/client/react/hoc'
import { Container, Row, Col } from 'reactstrap'
import './TaskManagementContainer.css'
import { AllBoardsQuery } from './Queries'
import withApolloClient from '../withApolloClient'
import BoardActionsMenu from './TaskComponents/BoardActionsMenu'
import TaskManagementViewContainer from './TaskManagementViewContainer'
import { getDefaultTaskColumnConfig } from './taskTableConfig'

// The sidebar query no longer carries each board's columns -- only the active board's are
// ever used, and nesting them cost a query per board. TaskManagementViewContainer fetches
// the active board's columns and appends them to this base config.
//
// My-Tasks-ness is derived from the route (`boardId`), never from whether the board happens
// to already be in `boards`. AllBoardsQuery pages in 30 at a time and resolves independently
// of the task list, so on a hard refresh -- or for a user with more than 30 boards -- a real
// board id can be looked up against an empty or incomplete `boards` list. Deriving from
// `boards.find(...)` would then read as "no active board", handing the board view the My
// Tasks aggregate column (and its server-side FK prefetch) plus the My Tasks preference key,
// sometimes permanently. `TaskManagementViewContainer` derives `isBoardView` from `boardId`
// the same way, and the two must agree or the config here silently mismatches which query
// variables and preferences it actually renders with.
const getTaskBoardColumnConfig = (boards = [], boardId) => {
    const isMyTasks = !boardId || boardId === "tasks"
    const activeBoard = isMyTasks ? null : boards.find(({ node }) => node.id === boardId)?.node || null

    return {
        activeBoard,
        // My Tasks spans every board, so it cannot show a board's FK columns
        // individually -- the aggregate column stands in for them there only.
        defaultColumnConfig: getDefaultTaskColumnConfig({ includeRelatedObjects: isMyTasks }),
        tablePreferenceId: isMyTasks ? "my_tasks_board_preferences" : `task_board_${boardId}_preferences`,
    }
}


// Small overlay on the board's tile marking who can see it. `isPrivate` is only rendered
// when it actually came back as a boolean -- a board whose visibility we don't know is
// better left unlabelled than shown as public, since the model defaults to private.
function BoardVisibilityBadge({ isPrivate }) {
    if (typeof isPrivate !== "boolean") {
        return null
    }
    const label = isPrivate ? "Private board" : "Public board"

    return (
        <span className={`board-visibility-badge ${isPrivate ? "is-private" : "is-public"}`} title={label}>
            <i className={isPrivate ? "fa fa-lock" : "fa fa-globe"} aria-hidden="true" />
            <span className="sr-only">{label}</span>
        </span>
    )
}


// Placeholder rows shown while the first page of boards is in flight. Matching the real
// item's shape keeps the list from jumping when the data lands.
const BOARD_SKELETON_ROWS = [0, 1, 2, 3]

// Exported unwrapped for tests -- the default export is bound to the boards query, which
// makes the loading states awkward to drive.
export function TaskManagementContainer({ allBoards, loading, loadMoreEntries, currentUser }) {
    const { boardId } = useParams()
    const boards = allBoards?.edges || []
    const loadedBoardCount = boards.length + 1
    const totalBoardCount = (allBoards?.pageInfo?.length || boards.length) + 1
    // The query sets notifyOnNetworkStatusChange, so `loading` also covers fetchMore.
    // Having boards already is what separates the first load from paging.
    const isInitialBoardLoad = loading && boards.length === 0
    const isLoadingMoreBoards = loading && boards.length > 0
    const { activeBoard, defaultColumnConfig, tablePreferenceId } = useMemo(
        () => getTaskBoardColumnConfig(boards, boardId),
        [boards, boardId])
    const activeBoardTitle = activeBoard?.title || "My Tasks"

    return (
        <div className="TaskManagementContainer">
            <Container fluid>
                <Row>
                    <Col xs="2" className="TaskBoardColumn">
                        <aside className="task-board-sidebar">
                            <div className="task-board-sidebar-header">
                                <div>
                                    <div className="task-board-sidebar-eyebrow">Workspace</div>
                                    <h4 className="task-board-sidebar-title">Boards</h4>
                                </div>
                                <span className="task-board-sidebar-count">
                                    {isInitialBoardLoad
                                        ? <i className="fa fa-spinner fa-spin" aria-hidden="true" />
                                        : loadedBoardCount}
                                </span>
                            </div>
                            <div className="task-board-sidebar-meta" aria-live="polite">
                                {isInitialBoardLoad
                                    ? "Loading boards..."
                                    : `Showing ${loadedBoardCount} of ${totalBoardCount} boards`}
                            </div>
                            <div className="task-board-sidebar-current">
                                <span className="task-board-sidebar-current-label">Viewing</span>
                                <span className="task-board-sidebar-current-value">{activeBoardTitle}</span>
                            </div>
                            <ul className="TaskBoardList">
                                <li className={!boardId || boardId === 'tasks' ? 'active' : ''}>
                                    <div className="board-sidebar-item">
                                        <Link className="board-sidebar-link" to="/boards/tasks/">
                                            <span className="board-sidebar-icon"><i className="fa fa-inbox" aria-hidden="true" /></span>
                                            <span className="board-sidebar-content">
                                                <span className="board-sidebar-title-text">My Tasks</span>
                                                <span className="board-sidebar-subtitle">Personal task queue</span>
                                            </span>
                                        </Link>
                                    </div>
                                </li>
                                {boards.map((boardEdge) => (
                                    <li className={boardId === boardEdge.node.id ? 'active' : ''} key={boardEdge.node.id}>
                                        <div className="board-sidebar-item">
                                            <Link className="board-sidebar-link" to={`/boards/${boardEdge.node.id}/tasks/`}>
                                                <span className="board-sidebar-icon">
                                                    <i className="fa fa-columns" aria-hidden="true" />
                                                    <BoardVisibilityBadge isPrivate={boardEdge.node.isPrivate} />
                                                </span>
                                                <span className="board-sidebar-content">
                                                    <span className="board-sidebar-title-text">{boardEdge.node.title}</span>
                                                    <span className="board-sidebar-subtitle">
                                                        {boardEdge.node.description || `Board #${boardEdge.node.pk}`}
                                                    </span>
                                                </span>
                                            </Link>
                                            <span className="board-edit-icon board-actions-wrapper" onClick={(e) => e.stopPropagation()}>
                                                <BoardActionsMenu boardId={boardEdge.node.id} />
                                            </span>
                                        </div>
                                    </li>
                                ))}
                                {isInitialBoardLoad && BOARD_SKELETON_ROWS.map((rowIndex) => (
                                    <li key={`board-skeleton-${rowIndex}`} aria-hidden="true">
                                        <div className="board-sidebar-item board-sidebar-item-skeleton">
                                            <span className="board-sidebar-link">
                                                <span className="board-sidebar-icon board-skeleton-shape" />
                                                <span className="board-sidebar-content">
                                                    <span className="board-skeleton-shape board-skeleton-line-title" />
                                                    <span className="board-skeleton-shape board-skeleton-line-subtitle" />
                                                </span>
                                            </span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            {allBoards?.pageInfo?.hasNextPage && (
                                <button
                                    type="button"
                                    className="task-board-load-more"
                                    onClick={loadMoreEntries}
                                    disabled={isLoadingMoreBoards}
                                >
                                    <span>{isLoadingMoreBoards ? "Loading boards..." : "Load more boards"}</span>
                                    <i
                                        className={isLoadingMoreBoards ? "fa fa-spinner fa-spin" : "fa fa-arrow-down"}
                                        aria-hidden="true"
                                    />
                                </button>
                            )}
                        </aside>
                    </Col>
                    <Col xs="10" className="TaskListColumn">
                        <TaskManagementViewContainer currentUser={currentUser} defaultColumnConfig={defaultColumnConfig} tablePreferenceId={tablePreferenceId} />
                    </Col>
                </Row>
            </Container>
        </div>
    )
}

export default graphql(AllBoardsQuery, {
    options: ({ searchTerm }) => ({
        variables: {
            searchTerm,
            orderBy: ["date_added"],
            isArchived: false,
            first: 30
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-and-network'
    }),
    props: ({ data: { loading, allBoards, fetchMore, searchTerm, variables, refetch } }) => (
        {
            loading,
            allBoards,
            variables,
            refetch,
            refetchDriversQuery: () => {
                //No apollo function that refetches in place with pagination considered so this function instead
                let currentLength = allBoards && allBoards.edges ? allBoards.edges.length : 1
                return fetchMore({
                    query: AllBoardsQuery,
                    variables: {
                        ...variables,
                        first: currentLength,
                    },
                    updateQuery: (previousResult, { fetchMoreResult }) => {
                        const newEdges = fetchMoreResult.allBoards.edges
                        const pageInfo = fetchMoreResult.allBoards.pageInfo
                        return {
                            allBoards: {
                                edges: [...newEdges],
                                pageInfo,
                                searchTerm: previousResult.searchTerm,
                                __typename: previousResult.allBoards.__typename
                            },
                        }
                    },
                })
            },
            loadMoreEntries: () => {
                return fetchMore({
                    query: AllBoardsQuery,
                    variables: {
                        cursor: allBoards.pageInfo.endCursor,
                        ...variables
                    },
                    updateQuery: (previousResult, { fetchMoreResult }) => {
                        const newEdges = fetchMoreResult.allBoards.edges
                        const pageInfo = fetchMoreResult.allBoards.pageInfo

                        return {
                            // Put the new comments at the end of the list and update `pageInfo`
                            // so we have the new `endCursor` and `hasNextPage` values
                            allBoards: {
                                edges: [...previousResult.allBoards.edges, ...newEdges],
                                pageInfo,
                                searchTerm: previousResult.searchTerm,
                                __typename: previousResult.allBoards.__typename
                            },
                        }
                    },
                })
            },
        }
    )
})(withApolloClient(TaskManagementContainer))
